<script lang="ts">
	import type { i18n, SvelteAsr, Mediator } from 'types/common'
	import type { ValueOf } from 'type-fest'

	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import Button from '@isoftdata/svelte-button'

	import Input from '@isoftdata/svelte-input'
	import Modal from '@isoftdata/svelte-modal'
	import UserGroup from '@isoftdata/svelte-user-group'
	import SaveResetButton from '@isoftdata/svelte-save-reset-button'
	import UserPrompt from '@isoftdata/svelte-user-prompt'

	import { getContext, tick } from 'svelte'
	import { CrudRuneStore } from '@isoftdata/svelte-store-crud'
	import { v4 as uuid } from '@lukeed/uuid'
	import { graphql, type GroupConfigData$result, type PermissionLevel } from '$houdini'
	import pMap from 'p-map'
	import { AddRemoveStore } from 'stores/add-remove-store.svelte'

	export type RequiredGroupType = {
		id: number | null
		name: string | null
		permissions: Array<{
			id: number
			category: string
			displayName: string
			codeName: string
			description: string
			value: Uppercase<ValueOf<typeof PermissionLevel>>
		}>
		userAccounts: Array<{
			id: number
			name: string
			isMember: boolean
		}>
		uuid: string
	}

	interface Props {
		asr: SvelteAsr
		groups: Array<RequiredGroupType>
		permissions: GroupConfigData$result['permissions']
		userAccounts: GroupConfigData$result['userAccounts']
	}

	let { asr, groups = $bindable(), permissions, userAccounts }: Props = $props()

	let selectedGroup: RequiredGroupType | null = $state(groups[0])
	let groupNameInput: HTMLInputElement | undefined = $state()
	let groupModal = $state({
		show: false,
		newGroup: false,
		groupName: '',
	})
	let userPrompt: UserPrompt | undefined = $state()

	const mediator = getContext<Mediator>('mediator')
	const { t: translate } = getContext<i18n>('i18next')

	const groupCrudStore = new CrudRuneStore<RequiredGroupType, 'uuid'>('uuid')
	const memberAddRemoveStore = new AddRemoveStore<number>()

	const createGroupMutation = graphql(`
		mutation CreateGroup($input: NewGroup!) {
			createGroup(input: $input) {
				id
			}
		}
	`)

	const updateGroupMutation = graphql(`
		mutation UpdateGroup($input: GroupUpdate!) {
			updateGroup(input: $input) {
				id
			}
		}
	`)

	const deleteGroupMutation = graphql(`
		mutation DeleteGroup($deleteGroupId: Int!) {
			deleteGroup(id: $deleteGroupId)
		}
	`)

	async function openGroupModal(newGroup: boolean) {
		groupModal = {
			show: true,
			newGroup,
			groupName: newGroup ? '' : (selectedGroup?.name ?? ''),
		}
		await tick()
		groupNameInput?.focus()
	}

	function createGroup() {
		const newGroup: RequiredGroupType = {
			id: null,
			name: groupModal.groupName,
			userAccounts: userAccounts.map(userAccount => ({
				id: userAccount.id,
				name: userAccount.name,
				isMember: false,
			})),
			permissions: permissions.map(permission => ({
				...permission,
				value: 'NONE' as const,
			})),
			uuid: uuid(),
		}
		groups.push(newGroup)
		groupCrudStore.create(newGroup)
		selectedGroup = newGroup
	}

	async function saveChanges() {
		try {
			await Promise.all([
				groupCrudStore.createdValues.length > 0
					? pMap(
							groupCrudStore.createdValues.filter(
								(group): group is RequiredGroupType & { name: string } => !!group.name,
							),
							async input => {
								await createGroupMutation.mutate({
									input: {
										name: input.name,
										permissions: input.permissions.map(groupPermission => ({
											permissionId: groupPermission.id,
											level: groupPermission.value,
										})),
										userAccounts: memberAddRemoveStore.getAddIds(input.uuid),
									},
								})
							},
							{ concurrency: 2 },
						)
					: null,
				groupCrudStore.updatedValues.length > 0
					? pMap(
							groupCrudStore.updatedValues.filter(
								(group): group is RequiredGroupType & { id: number } => group.id !== null,
							),
							async input => {
								await updateGroupMutation.mutate({
									input: {
										id: input.id,
										name: input.name,
										permissions: {
											additions: input.permissions.map(groupPermission => ({
												permissionId: groupPermission.id,
												level: groupPermission.value,
											})),
											removals: [],
										},
										users: {
											additions: memberAddRemoveStore.getAddIds(input.uuid),
											removals: memberAddRemoveStore.getRemoveIds(input.uuid),
										},
									},
								})
							},
							{ concurrency: 2 },
						)
					: null,
				groupCrudStore.deletedValues.length > 0
					? pMap(
							groupCrudStore.deletedValues.filter(
								(group): group is RequiredGroupType & { id: number } => group.id !== null,
							),
							async input => {
								await deleteGroupMutation.mutate({
									deleteGroupId: input.id,
								})
							},
							{ concurrency: 2 },
						)
					: null,
			])

			mediator.publish('showMessage', {
				heading: translate('common:savedHeading', 'Saved!'),
				message: translate(
					'configuration.userGroup.savedMessage',
					'Group membership & permissions saved successfully.',
				),
				color: 'success',
				time: 10,
			})
			asr.go(null, { lastSavedTime: Date.now(), lastResetTime: null }, { inherit: true })
		} catch (err: any) {
			mediator.publish('showMessage', {
				heading: translate('configuration.userGroup.errorSavingGroups', 'Error Saving Groups'),
				message: err.message,
				color: 'danger',
				time: false,
			})
		}
	}

	export function canLeaveState() {
		return (
			!hasUnsavedChanges ||
			confirm(
				translate(
					'common:canLeaveState',
					'You have unsaved changes. Are you sure you want to leave? All unsaved changes will be lost.',
				),
			)
		)
	}

	const selectedGroupIndex = $derived(
		selectedGroup ? groups.findIndex(group => group.uuid === selectedGroup?.uuid) : -1,
	)
	const hasUnsavedChanges = $derived(groupCrudStore.hasChanges())
	const displayGroups = $derived(groups.filter(group => !groupCrudStore.isDeleted(group)))
</script>

<div class="row align-items-end">
	<div class="col-12 col-sm-auto order-sm-last mb-1 ms-sm-auto">
		<SaveResetButton
			disabled={!hasUnsavedChanges}
			resetHref={asr.makePath(null, { lastResetTime: Date.now(), lastSavedTime: null })}
			save={saveChanges}
		></SaveResetButton>
	</div>
	<div class="col-12 col-lg-3">
		<Autocomplete
			label={translate('common:group', 'Group')}
			options={displayGroups}
			value={selectedGroup}
			getLabel={option => option?.name ?? ''}
			change={async option => {
				selectedGroup = option
			}}
		>
			{#snippet option({ option })}
				{#if option}
					{@const isCreated = groupCrudStore.isCreated(option)}
					{@const isUpdated = groupCrudStore.isUpdated(option)}
					<div class="d-flex justify-content-between">
						{option.name}
						{#if isCreated || isUpdated}
							<div class="text-end">
								<span
									class="badge text-white"
									class:text-bg-success={isCreated}
									class:text-bg-warning={isUpdated}
								>
									{isCreated ? translate('common:new', 'New') : translate('common:modified', 'Modified')}</span
								>
							</div>
						{/if}
					</div>
				{/if}
			{/snippet}
		</Autocomplete>
	</div>
	<div
		class="col-12 col-sm-auto d-flex justify-content-between mb-1"
		style="gap: 0.25rem"
	>
		<Button
			outline
			size="sm"
			icon="plus"
			color="success"
			onclick={() => openGroupModal(true)}
		>
			{translate('configuration.groups.newGroup', 'New Group')}...
		</Button>
		<Button
			outline
			size="sm"
			icon="pen"
			color="primary"
			disabled={!selectedGroup}
			onclick={() => openGroupModal(false)}
		>
			{translate('configuration.groups.editName', 'Edit Name')}...
		</Button>
		<Button
			outline
			size="sm"
			icon="trash"
			color="danger"
			disabled={!selectedGroup}
			onclick={async () => {
				if (
					selectedGroup &&
					(await userPrompt?.confirm(
						translate(
							'configuration.userGroup.deleteGroupAfterSavingThisCannotBeUndone.',
							'Delete group? After saving, this cannot be undone.',
						),
						{
							ok: translate('configuration.userGroup.deleteGroup', 'Delete Group'),
							okColor: 'danger',
						},
					))
				) {
					groupCrudStore.delete(selectedGroup)
					selectedGroup = groups.find(group => !groupCrudStore.isDeleted(group)) ?? null
				}
			}}
		>
			{translate('common:delete', 'Delete')}...
		</Button>
	</div>
</div>

{#if groups[selectedGroupIndex]}
	<UserGroup
		siteLabel={translate('common:store', 'Store')}
		bind:userAccounts={groups[selectedGroupIndex].userAccounts}
		bind:groupPermissions={groups[selectedGroupIndex].permissions}
		updateGroup={() => {
			if (selectedGroup) {
				groupCrudStore.update(selectedGroup)
			}
		}}
		addMember={ids => {
			if (selectedGroup) {
				memberAddRemoveStore.add(selectedGroup.uuid, ids)
			}
		}}
		removeMember={ids => {
			if (selectedGroup) {
				memberAddRemoveStore.remove(selectedGroup.uuid, ids)
			}
		}}
	/>
{:else}
	<div class="alert alert-info">
		{translate('configuration.groups.noGroupSelected', 'No group selected. Please select or create a new group.')}
	</div>
{/if}

<Modal
	confirmButtonType="submit"
	confirmButtonFormId="groupModalForm"
	title={groupModal.newGroup
		? translate('configuration.groups.enterNewGroupName', 'Enter New Group Name')
		: translate('configuration.groups.editGroupName', 'Edit Group Name')}
	confirmButtonText={groupModal.newGroup
		? translate('configuration.groups.createGroup', 'Create Group')
		: translate('common:save', 'Save')}
	cancelButtonText={translate('common:cancel', 'Cancel')}
	bind:show={groupModal.show}
	close={() => (groupModal.show = false)}
>
	<form
		id="groupModalForm"
		onsubmit={event => {
			event.preventDefault()
			if (groupModal.newGroup) {
				createGroup()
			} else if (selectedGroup) {
				groups[selectedGroupIndex].name = groupModal.groupName
				groupCrudStore.update(selectedGroup)
			}
			groupModal = {
				show: false,
				newGroup: false,
				groupName: '',
			}
		}}
	>
		<Input
			label={translate('configuration.groups.groupName', 'Group Name')}
			bind:value={groupModal.groupName}
			bind:input={groupNameInput}
		/>
	</form>
</Modal>

<UserPrompt bind:this={userPrompt} />
